import { useFormField } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuGroupTitle, DropdownMenuItem, DropdownMenuTrigger } from '@calendly/ui/components/dropdown-menu';
import { IconCheck } from '@calendly/ui/components/glyphs/IconCheck';
import { IconClock } from '@calendly/ui/components/glyphs/IconClock';
import { IconEdit } from '@calendly/ui/components/glyphs/IconEdit';
import { IconExternalLink } from '@calendly/ui/components/glyphs/IconExternalLink';
import { ArrowChevronDownMiniIcon } from '@calendly/ui/components/icons';
import { BodyText } from '@calendly/ui/components/typography';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import { colorIconSecondary, colorIconStandard } from '@calendly/ui/theme';
import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getAvailabilityFromRule } from 'components/event_type_edit';
import { useEventType } from 'features/event_type_provider';
import { useTrackAvailabilityInteraction } from 'hooks/analytics_events/use_track_availability_interaction';
import { AVAILABILITY_SCHEDULES_PATH } from 'shared/constants/availability_browser_urls';
import { formatV4ScheduleSummary } from './format_v4_schedule_summary';
import { etEditorMenuLayerStyle } from '../../name_and_color_fields/et_editor_menu_layer';
import { getScheduleName } from '../available_hours/components/schedule_controls/helpers';
import { useActiveSchedule } from '../available_hours/components/schedule_controls/use_active_schedule';
const CUSTOM_OPTION = 'custom';
const menuContentCss = "muh1y88";
const groupTitleCss = "gb8yi1h";
const menuItemRowCss = "m1e1e5jn";
const menuItemContentCss = "m1wzru53";
const titleRowCss = "t1urn50a";
const menuItemTrailingCss = "mziz0lt";

/* Match BodyText medium line box so icon sits on the title line, not the description */
const menuItemIconCss = "mbpz594";
const getDefaultSuffix = () => I18n.t('publisher.event_type_editor.sections.availability.schedule.actions.default_schedule', {
  name: ''
}).trim();
const ScheduleMenuItem = ({
  description,
  icon,
  isDefault = false,
  isSelected,
  label,
  onSelect
}) => <DropdownMenuItem onSelect={onSelect}>
    <div className={menuItemRowCss}>
      <span className={menuItemIconCss}>
        <UiIcon color={colorIconSecondary} icon={icon} size="small" />
      </span>
      <div className={menuItemContentCss}>
        <div className={titleRowCss}>
          <BodyText size="medium">{label}</BodyText>
          {isDefault && <BodyText color="muted" size="medium">
              {getDefaultSuffix()}
            </BodyText>}
        </div>
        {description && <BodyText color="muted" size="small">
            {description}
          </BodyText>}
      </div>
      {isSelected && <div className={menuItemTrailingCss}>
          <UiIcon color={colorIconStandard} icon={IconCheck} size="small" />
        </div>}
    </div>
  </DropdownMenuItem>;
const getCustomHoursLabel = () => I18n.t('publisher.event_type_editor.sections.available_hours.custom_hours_label');
const getTriggerLabel = ({
  usingCustomHours,
  selectedSchedule,
  isPrimarySchedule
}) => {
  if (usingCustomHours) {
    return getCustomHoursLabel();
  }
  if (selectedSchedule?.name) {
    return getScheduleName(isPrimarySchedule, selectedSchedule.name);
  }
  return I18n.t('publisher.event_type_editor_crud.availability.default_hours_label');
};
export const V4SchedulesDropdown = ({
  formPath
}) => {
  const eventType = useEventType();
  const history = useHistory();
  const [{
    value
  }, _, {
    setValue
  }] = useFormField({
    name: formPath
  });
  const [isOpen, setIsOpen] = useState(false);
  const trackETEAvailabilityInteraction = useTrackAvailabilityInteraction({
    eventType,
    location: 'et_editor'
  });
  const {
    activeSchedule
  } = useActiveSchedule({
    formPath
  });
  const {
    activeAvailabilityRuleId,
    usingCustomHours,
    customRuleId,
    ownerAvailabilityRules
  } = value;
  const primaryRuleId = ownerAvailabilityRules?.primaryRuleId || ownerAvailabilityRules?.primary_rule_id;
  const memberSchedules = ownerAvailabilityRules?.rules;
  const primarySchedule = memberSchedules?.find(({
    id
  }) => String(id) === String(primaryRuleId));
  const selectedSchedule = usingCustomHours ? null : memberSchedules?.find(({
    id
  }) => String(id) === String(activeAvailabilityRuleId)) || primarySchedule;
  const isPrimarySchedule = Boolean(selectedSchedule && String(selectedSchedule.id) === String(primaryRuleId));
  const resolvedActiveRuleId = usingCustomHours ? null : selectedSchedule?.id;
  const triggerLabel = getTriggerLabel({
    usingCustomHours,
    selectedSchedule,
    isPrimarySchedule
  });
  const TriggerIcon = usingCustomHours ? IconEdit : IconClock;
  const handleScheduleChange = useCallback(selectedScheduleId => {
    setIsOpen(false);
    if (selectedScheduleId === CUSTOM_OPTION) {
      if (usingCustomHours) return;
      trackETEAvailabilityInteraction({
        action: 'customize_schedule_for_this_event_type_only'
      });
      setValue({
        ...value,
        ...getAvailabilityFromRule(activeSchedule || primarySchedule),
        usingCustomHours: true,
        usingSecondarySchedule: false,
        activeAvailabilityRuleId: customRuleId
      });
      return;
    }
    const selectedScheduleOption = memberSchedules.find(({
      id
    }) => String(id) === String(selectedScheduleId));
    if (!selectedScheduleOption) return;
    if (!usingCustomHours && String(activeAvailabilityRuleId) === String(selectedScheduleId)) return;
    const isPrimaryRule = String(primaryRuleId) === String(selectedScheduleOption.id);
    if (isPrimaryRule) {
      trackETEAvailabilityInteraction({
        action: 'reset_to_default_schedule'
      });
    }
    setValue({
      ...value,
      usingCustomHours: false,
      usingSecondarySchedule: true,
      activeAvailabilityRuleId: selectedScheduleOption.id,
      ...getAvailabilityFromRule(selectedScheduleOption)
    });
  }, [activeAvailabilityRuleId, activeSchedule, customRuleId, memberSchedules, primaryRuleId, primarySchedule, setValue, trackETEAvailabilityInteraction, usingCustomHours, value]);
  const handleManageSchedules = useCallback(() => {
    setIsOpen(false);
    history.push(AVAILABILITY_SCHEDULES_PATH);
  }, [history]);
  if (!(ownerAvailabilityRules && memberSchedules && primarySchedule)) return null;
  return <div data-testid="v4-schedules-dropdown">
      <DropdownMenu isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <DropdownMenuTrigger>
          <Button aria-expanded={isOpen} aria-label={I18n.t('publisher.event_type_editor.sections.availability.schedule.title')} decoration="secondary-outline" onClick={() => setIsOpen(open => !open)} size="small">
            <UiIcon icon={TriggerIcon} size="small" />
            {triggerLabel}
            <ArrowChevronDownMiniIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className={menuContentCss} placement="bottom end" menuStyle={etEditorMenuLayerStyle} mobileMenuStyle={etEditorMenuLayerStyle}>
          <DropdownMenuGroup>
            <DropdownMenuGroupTitle className={groupTitleCss}>
              {I18n.t('publisher.event_type_editor.sections.available_hours.schedules_label')}
            </DropdownMenuGroupTitle>
            {memberSchedules.map(schedule => {
            const isPrimaryRule = String(schedule.id) === String(primaryRuleId);
            const isSelected = !usingCustomHours && String(resolvedActiveRuleId) === String(schedule.id);
            return <ScheduleMenuItem key={schedule.id} description={formatV4ScheduleSummary(schedule)} icon={IconClock} isDefault={isPrimaryRule} isSelected={isSelected} label={schedule.name} onSelect={() => handleScheduleChange(schedule.id)} />;
          })}
          </DropdownMenuGroup>
          <DropdownMenuGroup>
            <ScheduleMenuItem description={I18n.t('publisher.event_type_editor_crud.availability.custom_hours_description')} icon={IconEdit} isSelected={usingCustomHours} label={getCustomHoursLabel()} onSelect={() => handleScheduleChange(CUSTOM_OPTION)} />
          </DropdownMenuGroup>
          <DropdownMenuGroup>
            <DropdownMenuItem onSelect={handleManageSchedules} data-testid="v4-manage-schedules">
              <div className={menuItemRowCss}>
                <span className={menuItemIconCss}>
                  <UiIcon color={colorIconSecondary} icon={IconExternalLink} size="small" />
                </span>
                <div className={menuItemContentCss}>
                  <div className={titleRowCss}>
                    <BodyText size="medium">
                      {I18n.t('publisher.event_type_editor_crud.availability.manage_schedules')}
                    </BodyText>
                  </div>
                </div>
              </div>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>;
};

require("./v4_schedules_dropdown.wyw-in-js.css!=!../../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./v4_schedules_dropdown.js");